import type {
  InsuranceArticleCategory,
  InsuranceArticleContent,
  InsuranceProductConfig,
} from './insuranceArticleTypes';

export const PET_LAST_MODIFIED = 'June 2, 2026';
export const PET_BASE_PATH = '/knowledge-base/pet';

export const PET_ARTICLE_CATEGORIES: InsuranceArticleCategory[] = [
  {
    name: 'Fundamentals',
    articles: [
      {
        title: 'What is pet insurance?',
        slug: 'what-is-pet-insurance',
        href: '/knowledge-base/pet/what-is-pet-insurance',
        description:
          'Learn how pet insurance works, what it pays for, and how reimbursement-based coverage differs from human health insurance.',
        keywords: ['what is pet insurance', 'pet insurance basics', 'how pet insurance works'],
        status: 'published',
      },
      {
        title: 'Is pet insurance worth it?',
        slug: 'is-pet-insurance-worth-it',
        href: '/knowledge-base/pet/is-pet-insurance-worth-it',
        description:
          'Weigh the cost of monthly premiums against unexpected vet bills and see when pet insurance makes financial sense.',
        keywords: ['is pet insurance worth it', 'pet insurance value', 'should I get pet insurance'],
        status: 'published',
      },
      {
        title: 'What does pet insurance cover?',
        slug: 'what-does-pet-insurance-cover',
        href: '/knowledge-base/pet/what-does-pet-insurance-cover',
        description:
          'See what most accident and illness pet insurance policies cover, from emergency surgery to chronic conditions.',
        keywords: ['pet insurance coverage', 'what pet insurance covers', 'accident illness coverage'],
        status: 'published',
      },
      {
        title: 'What does pet insurance not cover?',
        slug: 'what-does-pet-insurance-not-cover',
        href: '/knowledge-base/pet/what-does-pet-insurance-not-cover',
        description:
          'Understand common pet insurance exclusions, including pre-existing conditions, grooming, and routine care.',
        keywords: ['pet insurance exclusions', 'what pet insurance does not cover', 'pet insurance limits'],
        status: 'published',
      },
      {
        title: 'How do pet insurance waiting periods work?',
        slug: 'how-do-pet-insurance-waiting-periods-work',
        href: '/knowledge-base/pet/how-do-pet-insurance-waiting-periods-work',
        description:
          'Understand accident, illness, and orthopedic waiting periods and how they affect when your pet insurance coverage actually starts.',
        keywords: ['pet insurance waiting period', 'waiting period pet insurance', 'when coverage starts'],
        status: 'published',
      },
      {
        title: 'Does pet insurance work out of state?',
        slug: 'does-pet-insurance-work-out-of-state',
        href: '/knowledge-base/pet/does-pet-insurance-work-out-of-state',
        description:
          'Learn whether pet insurance covers vet visits when you travel out of state or relocate, and what to know about nationwide reimbursement.',
        keywords: ['pet insurance out of state', 'pet insurance travel', 'vet coverage nationwide'],
        status: 'published',
      },
    ],
  },
  {
    name: 'Plans & Pricing',
    articles: [
      {
        title: 'How much does pet insurance cost in Texas?',
        slug: 'how-much-does-pet-insurance-cost-in-texas',
        href: '/knowledge-base/pet/how-much-does-pet-insurance-cost-in-texas',
        description:
          'Estimate typical pet insurance premiums in Texas and learn what drives price for dogs and cats.',
        keywords: ['pet insurance cost Texas', 'pet insurance price', 'Texas pet insurance rates'],
        status: 'published',
      },
      {
        title: 'Accident-only vs. accident and illness pet insurance',
        slug: 'accident-only-vs-accident-and-illness-pet-insurance',
        href: '/knowledge-base/pet/accident-only-vs-accident-and-illness-pet-insurance',
        description:
          'Compare accident-only and accident-and-illness plans to decide which level of coverage fits your pet.',
        keywords: ['accident only pet insurance', 'accident illness pet insurance', 'pet insurance plan types'],
        status: 'published',
      },
      {
        title: 'How do pet insurance deductibles work?',
        slug: 'how-do-pet-insurance-deductibles-work',
        href: '/knowledge-base/pet/how-do-pet-insurance-deductibles-work',
        description:
          'Learn how annual and per-incident deductibles affect your premium and out-of-pocket vet costs.',
        keywords: ['pet insurance deductible', 'annual deductible', 'per incident deductible'],
        status: 'published',
      },
      {
        title: 'Pet insurance reimbursement levels (70/80/90%)',
        slug: 'pet-insurance-reimbursement-levels',
        href: '/knowledge-base/pet/pet-insurance-reimbursement-levels',
        description:
          'Understand 70%, 80%, and 90% reimbursement options and how they change your monthly premium and claim payouts.',
        keywords: ['pet insurance reimbursement', '70 80 90 percent', 'reimbursement level'],
        status: 'published',
      },
      {
        title: 'Annual coverage limits in pet insurance',
        slug: 'annual-coverage-limits-in-pet-insurance',
        href: '/knowledge-base/pet/annual-coverage-limits-in-pet-insurance',
        description:
          'Compare annual, per-condition, and lifetime coverage limits and learn how caps affect reimbursement on large vet claims.',
        keywords: ['pet insurance annual limit', 'coverage limit pet insurance', 'unlimited pet insurance'],
        status: 'published',
      },
      {
        title: 'Multi-pet insurance coverage',
        slug: 'multi-pet-insurance-coverage',
        href: '/knowledge-base/pet/multi-pet-insurance-coverage',
        description:
          'How insuring multiple dogs or cats works — separate policies, shared deductibles, discounts, and what to compare before enrolling.',
        keywords: ['multi pet insurance', 'insure multiple pets', 'two pet insurance policy'],
        status: 'published',
      },
    ],
  },
  {
    name: 'Life Stages & Health',
    articles: [
      {
        title: 'Are pre-existing conditions covered by pet insurance?',
        slug: 'are-pre-existing-conditions-covered-by-pet-insurance',
        href: '/knowledge-base/pet/are-pre-existing-conditions-covered-by-pet-insurance',
        description:
          'Find out how insurers define pre-existing conditions and why enrolling early matters for Texas pet owners.',
        keywords: ['pre-existing conditions pet insurance', 'pet insurance pre-existing', 'curable conditions'],
        status: 'published',
      },
      {
        title: 'Pet insurance for puppies and kittens',
        slug: 'pet-insurance-for-puppies-and-kittens',
        href: '/knowledge-base/pet/pet-insurance-for-puppies-and-kittens',
        description:
          'Why starting coverage when your pet is young can lock in better rates and fewer exclusions over time.',
        keywords: ['puppy insurance', 'kitten insurance', 'pet insurance young pets'],
        status: 'published',
      },
      {
        title: 'Pet insurance for senior pets',
        slug: 'pet-insurance-for-senior-pets',
        href: '/knowledge-base/pet/pet-insurance-for-senior-pets',
        description:
          'Options and trade-offs for insuring older dogs and cats, including waiting periods and premium increases.',
        keywords: ['senior pet insurance', 'old dog insurance', 'insurance for older cats'],
        status: 'published',
      },
      {
        title: 'Does pet insurance cover dental?',
        slug: 'does-pet-insurance-cover-dental',
        href: '/knowledge-base/pet/does-pet-insurance-cover-dental',
        description:
          'Learn when dental cleanings, extractions, and accident-related tooth injuries are covered by pet insurance.',
        keywords: ['pet insurance dental', 'dental coverage pets', 'tooth extraction pet insurance'],
        status: 'published',
      },
      {
        title: 'Does pet insurance cover cancer?',
        slug: 'does-pet-insurance-cover-cancer',
        href: '/knowledge-base/pet/does-pet-insurance-cover-cancer',
        description:
          'Find out when cancer treatment is covered by accident-and-illness pet insurance, including chemo, surgery, and common exclusions.',
        keywords: ['pet insurance cancer', 'dog cancer insurance', 'cat cancer coverage'],
        status: 'published',
      },
      {
        title: 'Does pet insurance cover hip dysplasia?',
        slug: 'does-pet-insurance-cover-hip-dysplasia',
        href: '/knowledge-base/pet/does-pet-insurance-cover-hip-dysplasia',
        description:
          'Learn when hip dysplasia surgery and treatment are covered, breed considerations, and orthopedic waiting period rules.',
        keywords: ['hip dysplasia pet insurance', 'pet insurance orthopedic', 'dog hip dysplasia coverage'],
        status: 'published',
      },
      {
        title: 'Does pet insurance cover emergency vet visits?',
        slug: 'does-pet-insurance-cover-emergency-vet-visits',
        href: '/knowledge-base/pet/does-pet-insurance-cover-emergency-vet-visits',
        description:
          'See how pet insurance handles emergency exams, after-hours clinics, stabilization, and hospitalization for accidents and illness.',
        keywords: ['emergency vet pet insurance', 'pet insurance ER visit', 'after hours vet coverage'],
        status: 'published',
      },
      {
        title: 'Does pet insurance cover hereditary conditions?',
        slug: 'does-pet-insurance-cover-hereditary-conditions',
        href: '/knowledge-base/pet/does-pet-insurance-cover-hereditary-conditions',
        description:
          'Understand how insurers treat hereditary and congenital conditions, breed-specific rules, and enrollment timing for genetic issues.',
        keywords: ['hereditary conditions pet insurance', 'congenital pet insurance', 'genetic condition coverage'],
        status: 'published',
      },
    ],
  },
  {
    name: 'Buying & Claims',
    articles: [
      {
        title: 'Pet insurance vs. wellness plans',
        slug: 'pet-insurance-vs-wellness-plans',
        href: '/knowledge-base/pet/pet-insurance-vs-wellness-plans',
        description:
          'Compare accident-and-illness insurance with wellness plans for vaccines, exams, and routine preventive care.',
        keywords: ['pet insurance vs wellness plan', 'wellness plan pets', 'preventive care coverage'],
        status: 'published',
      },
      {
        title: 'How to choose pet insurance in Texas',
        slug: 'how-to-choose-pet-insurance-in-texas',
        href: '/knowledge-base/pet/how-to-choose-pet-insurance-in-texas',
        description:
          'A step-by-step guide to comparing Texas pet insurance carriers, limits, deductibles, and reimbursement rates.',
        keywords: ['choose pet insurance Texas', 'compare pet insurance', 'best pet insurance Texas'],
        status: 'published',
      },
      {
        title: 'How to file a pet insurance claim',
        slug: 'how-to-file-a-pet-insurance-claim',
        href: '/knowledge-base/pet/how-to-file-a-pet-insurance-claim',
        description:
          'Follow a clear claims checklist so you can submit vet invoices, medical records, and get reimbursed faster.',
        keywords: ['file pet insurance claim', 'pet insurance claims process', 'vet bill reimbursement'],
        status: 'published',
      },
      {
        title: 'Does pet insurance cover prescription medications?',
        slug: 'does-pet-insurance-cover-prescription-medications',
        href: '/knowledge-base/pet/does-pet-insurance-cover-prescription-medications',
        description:
          'Learn when prescription drugs, chronic medication, and prescription food are covered by pet insurance and what documentation claims need.',
        keywords: ['pet insurance prescriptions', 'pet medication coverage', 'prescription food pet insurance'],
        status: 'published',
      },
      {
        title: 'Pet insurance breed exclusions and restrictions',
        slug: 'pet-insurance-breed-exclusions-and-restrictions',
        href: '/knowledge-base/pet/pet-insurance-breed-exclusions-and-restrictions',
        description:
          'How breed-specific exclusions work, which breeds face restrictions, and what to check in your policy before you enroll your pet.',
        keywords: ['pet insurance breed exclusions', 'breed restrictions pet insurance', 'dangerous breed insurance'],
        status: 'published',
      },
    ],
  },
];

export const PET_ARTICLE_CONTENT: Record<string, InsuranceArticleContent> = {
  'what-is-pet-insurance': {
    slug: 'what-is-pet-insurance',
    title: 'What is pet insurance?',
    subtitle:
      'Pet insurance helps you pay for unexpected veterinary bills by reimbursing a portion of eligible costs after you meet your deductible.',
    metaTitle: 'What Is Pet Insurance? How It Works | GetSkimp',
    metaDescription:
      'Learn how pet insurance works in Texas — reimbursement models, deductibles, waiting periods, and what accident-and-illness plans cover for dogs and cats.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Pet insurance is a reimbursement-based policy that helps pay for veterinary care when your dog or cat gets sick or injured. You pay the vet upfront, submit a claim, and the insurer pays you back a percentage of eligible costs after your deductible is met. It is not the same as human health insurance — there is no network of in-network vets, and most plans do not cover routine wellness unless you add a separate wellness rider.',
        ],
      },
      {
        title: 'How pet insurance works',
        paragraphs: [
          'Most pet insurance policies follow a simple reimbursement model. You choose a plan with a monthly premium, annual deductible, reimbursement percentage (often 70%, 80%, or 90%), and annual coverage limit. When your pet needs care, you visit any licensed veterinarian in Texas or elsewhere, pay the bill, and file a claim with your insurer.',
          'After the insurer reviews your claim against your policy terms, they reimburse you for eligible expenses minus your deductible and any co-insurance share. For example, on an 80% plan with a $250 deductible, a $2,000 eligible surgery might net you roughly $1,400 back after the deductible is applied.',
        ],
        numbered: [
          'Choose your coverage level — accident-only or accident and illness.',
          'Pay your monthly premium to keep the policy active.',
          'Take your pet to the vet and pay the invoice at the time of service.',
          'Submit the itemized bill and medical records to your insurer.',
          'Receive reimbursement for eligible costs according to your plan terms.',
        ],
      },
      {
        title: 'What pet insurance typically covers',
        bullets: [
          'Emergency vet visits and hospitalization after accidents or sudden illness.',
          'Surgeries, diagnostic tests (X-rays, blood work, MRIs), and prescription medications.',
          'Chronic conditions like allergies, diabetes, or arthritis if they develop after enrollment.',
          'Some hereditary and congenital conditions, depending on the carrier and breed.',
          'Alternative therapies such as acupuncture or hydrotherapy on select plans.',
        ],
      },
      {
        title: 'Pet insurance vs. other ways to pay for vet care',
        paragraphs: [
          'Some pet owners rely on savings accounts, credit cards, or CareCredit for vet bills. Those options work for smaller expenses, but a single emergency — such as a torn ACL or foreign body surgery — can cost $3,000 to $8,000 or more. Pet insurance spreads that risk across predictable monthly premiums instead of a single large out-of-pocket hit.',
          'Wellness plans and discount programs are different products. A wellness plan reimburses routine care like vaccines and annual exams, while pet insurance focuses on unexpected medical events. See [[pet insurance vs. wellness plans|/knowledge-base/pet/pet-insurance-vs-wellness-plans]] for a side-by-side comparison.',
        ],
      },
      {
        title: 'Key terms to know before you buy',
        table: {
          headers: ['Term', 'What it means'],
          rows: [
            ['Deductible', 'The amount you pay out of pocket before reimbursement kicks in each policy year.'],
            ['Reimbursement rate', 'The percentage of eligible vet costs the insurer pays back — commonly 70%, 80%, or 90%.'],
            ['Annual limit', 'The maximum the insurer will pay in a 12-month period; limits range from $5,000 to unlimited.'],
            ['Waiting period', 'Days after enrollment before coverage begins — often 2–14 days for accidents, longer for illnesses.'],
            ['Pre-existing condition', 'Any illness or injury that showed symptoms before your policy started or during a waiting period.'],
          ],
        },
      },
    ],
  },

  'is-pet-insurance-worth-it': {
    slug: 'is-pet-insurance-worth-it',
    title: 'Is pet insurance worth it?',
    subtitle:
      'For many Texas pet owners, insurance pays off when a single emergency vet bill would otherwise strain savings or force difficult financial choices.',
    metaTitle: 'Is Pet Insurance Worth It? Cost vs. Benefit | GetSkimp',
    metaDescription:
      'Is pet insurance worth the cost? Compare premiums, typical vet bills, and when accident-and-illness coverage makes sense for Texas dog and cat owners.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Pet insurance is worth it for many owners who want financial protection against large, unpredictable vet bills. If a $5,000 emergency surgery would be hard to cover out of pocket, a policy that costs $30 to $70 per month can provide meaningful peace of mind. It is less valuable if you have substantial savings dedicated to pet care and prefer to self-insure — but even then, insurance can preserve savings for other goals.',
        ],
      },
      {
        title: 'When pet insurance makes the most sense',
        bullets: [
          'You have a young, healthy pet and want to enroll before pre-existing conditions develop.',
          'Your breed is prone to costly conditions — hip dysplasia in large dogs, for example.',
          'You would struggle to pay $2,000 or more for an unexpected vet emergency without going into debt.',
          'You want the freedom to choose advanced treatments (MRI, specialist referral, surgery) without cost being the only factor.',
          'You live in an area with higher vet costs, such as Austin, Dallas, or Houston metro areas.',
        ],
      },
      {
        title: 'When you might skip it',
        paragraphs: [
          'If you already have several thousand dollars set aside specifically for pet emergencies and are comfortable absorbing a large bill, self-insuring may work. Pet insurance also becomes harder to justify for very senior pets with existing health issues, since premiums rise and pre-existing exclusions limit what the policy pays.',
          'Accident-only plans are cheaper but leave you exposed to illness costs, which account for most large claims over a pet\'s lifetime. Read [[what does pet insurance cover|/knowledge-base/pet/what-does-pet-insurance-cover]] and [[what does pet insurance not cover|/knowledge-base/pet/what-does-pet-insurance-not-cover]] before deciding.',
        ],
      },
      {
        title: 'Real-world cost comparison',
        table: {
          headers: ['Scenario', 'Without insurance', 'With insurance (est.)'],
          rows: [
            ['ACL surgery for a dog', '$3,500–$6,000 out of pocket', 'Premium + deductible; insurer pays 70–90% of eligible costs'],
            ['Foreign body ingestion', '$1,500–$4,000', 'Same reimbursement model after deductible'],
            ['Diabetes management (year 1)', '$2,000–$5,000 ongoing', 'Ongoing eligible costs reimbursed each year up to annual limit'],
            ['Monthly premium (Texas avg.)', '$0', '$25–$70 depending on pet age, breed, and plan'],
          ],
        },
      },
      {
        title: 'How to decide for your situation',
        numbered: [
          'Estimate your monthly budget for pet care, including food, routine vet visits, and an emergency fund.',
          'Get quotes for your pet\'s age, breed, and zip code — see [[how much pet insurance costs in Texas|/knowledge-base/pet/how-much-does-pet-insurance-cost-in-texas]].',
          'Compare at least two plan types: accident-only and accident-and-illness.',
          'Factor in whether you\'re insuring a puppy or kitten ([[enroll early|/knowledge-base/pet/pet-insurance-for-puppies-and-kittens]]) or a senior pet ([[senior pet options|/knowledge-base/pet/pet-insurance-for-senior-pets]]).',
          'Choose coverage if the premium is affordable and a single major claim would justify years of payments.',
        ],
      },
    ],
  },

  'how-much-does-pet-insurance-cost-in-texas': {
    slug: 'how-much-does-pet-insurance-cost-in-texas',
    title: 'How much does pet insurance cost in Texas?',
    subtitle:
      'Texas pet insurance premiums vary by species, breed, age, and plan design — but most dog and cat owners pay between $25 and $70 per month.',
    metaTitle: 'Pet Insurance Cost in Texas (2026 Guide) | GetSkimp',
    metaDescription:
      'How much does pet insurance cost in Texas? See average monthly premiums for dogs and cats, key pricing factors, and practical tips to lower your rate.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Most Texas pet owners pay $30 to $50 per month for accident-and-illness coverage on a young to middle-aged dog, and $15 to $35 per month for a cat. Accident-only plans cost less — often $10 to $25 per month — but do not cover illnesses. Your exact premium depends on breed, age, zip code, deductible, reimbursement level, and annual limit.',
        ],
      },
      {
        title: 'Average monthly premiums in Texas',
        table: {
          headers: ['Pet type', 'Accident & illness', 'Accident only'],
          rows: [
            ['Dog (young adult)', '$35–$55/mo', '$15–$25/mo'],
            ['Dog (senior, 8+ years)', '$60–$100+/mo', '$25–$40/mo'],
            ['Cat (young adult)', '$18–$30/mo', '$10–$18/mo'],
            ['Cat (senior)', '$35–$55/mo', '$18–$28/mo'],
          ],
        },
      },
      {
        title: 'What drives your premium',
        bullets: [
          'Species and breed — large breeds and breeds prone to genetic conditions cost more to insure.',
          'Age — premiums increase as pets get older; enrolling young locks in lower starting rates.',
          'Location — vet costs in Dallas, Austin, and Houston can push premiums slightly above rural Texas averages.',
          'Deductible — higher deductibles ($500 vs. $250) usually mean lower monthly premiums.',
          'Reimbursement rate — 90% reimbursement costs more than 70%; see [[reimbursement levels|/knowledge-base/pet/pet-insurance-reimbursement-levels]].',
          'Annual limit — unlimited coverage costs more than a $5,000 or $10,000 cap.',
        ],
      },
      {
        title: 'Ways to lower your pet insurance cost',
        paragraphs: [
          'Choosing a higher deductible and a 70% or 80% reimbursement rate are the fastest ways to reduce your monthly bill. Some carriers offer multi-pet discounts of 5% to 10% when you insure two or more pets on the same policy.',
          'Paying annually instead of monthly can save a small percentage on the total premium. Spaying or neutering your pet may also qualify for a modest discount with some insurers. Avoid accident-only plans unless you truly cannot afford illness coverage — illness claims are typically more frequent and expensive over a pet\'s lifetime.',
        ],
      },
      {
        title: 'Cost vs. coverage trade-offs',
        paragraphs: [
          'The cheapest plan is not always the best value. A low premium with a $1,000 deductible and 70% reimbursement means you pay more at claim time. Run the math on a realistic scenario — a $3,000 surgery — using different [[deductible|/knowledge-base/pet/how-do-pet-insurance-deductibles-work]] and reimbursement combinations before you commit.',
        ],
      },
    ],
  },

  'what-does-pet-insurance-cover': {
    slug: 'what-does-pet-insurance-cover',
    title: 'What does pet insurance cover?',
    subtitle:
      'Accident-and-illness pet insurance covers unexpected veterinary costs — from emergency surgery to chronic disease management — depending on your plan terms.',
    metaTitle: 'What Does Pet Insurance Cover? Full Guide | GetSkimp',
    metaDescription:
      'What does pet insurance cover? Learn what accident-and-illness plans pay for — surgery, diagnostics, medications, and chronic conditions — plus common limits.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Most accident-and-illness pet insurance policies cover unexpected injuries (broken bones, lacerations, toxin ingestion) and illnesses (infections, cancer, allergies, digestive issues) after waiting periods pass. Coverage includes diagnostics, surgery, hospitalization, prescription medications, and sometimes rehabilitation. Routine wellness, pre-existing conditions, and cosmetic procedures are generally excluded.',
        ],
      },
      {
        title: 'Accident coverage',
        bullets: [
          'Broken bones, sprains, and soft-tissue injuries from falls or accidents.',
          'Lacerations, bite wounds, and foreign object ingestion requiring surgery.',
          'Poisoning or toxin exposure requiring emergency treatment.',
          'Hit-by-car injuries and related trauma care.',
          'Emergency exam fees, X-rays, blood work, and hospitalization tied to the accident.',
        ],
      },
      {
        title: 'Illness coverage',
        bullets: [
          'Infections — ear, urinary tract, respiratory, and skin infections.',
          'Cancer — chemotherapy, radiation, and surgical tumor removal on most plans.',
          'Chronic conditions — allergies, arthritis, diabetes, kidney disease, and hypothyroidism if not pre-existing.',
          'Digestive issues — vomiting, diarrhea, pancreatitis, and inflammatory bowel disease.',
          'Hereditary and congenital conditions — coverage varies by breed and carrier; check policy details for your pet.',
        ],
      },
      {
        title: 'Additional benefits on many plans',
        paragraphs: [
          'Beyond core medical care, some policies include coverage for prescription food (when medically necessary), alternative therapies (acupuncture, chiropractic), and behavioral treatment for anxiety or compulsive disorders. [[Dental coverage|/knowledge-base/pet/does-pet-insurance-cover-dental]] is usually limited to accident-related tooth injuries unless you add a wellness rider.',
          'End-of-life care such as euthanasia may be covered on select plans, often with a small cap. Always read your policy\'s benefit schedule for specific dollar limits on each category.',
        ],
      },
      {
        title: 'Coverage by plan type',
        table: {
          headers: ['Benefit', 'Accident only', 'Accident & illness'],
          rows: [
            ['Broken leg from fall', 'Covered', 'Covered'],
            ['Ear infection', 'Not covered', 'Covered'],
            ['Cancer treatment', 'Not covered', 'Covered'],
            ['Annual vaccines', 'Not covered', 'Not covered (unless wellness add-on)'],
            ['Pre-existing allergy', 'Not covered', 'Not covered'],
          ],
        },
      },
    ],
  },

  'what-does-pet-insurance-not-cover': {
    slug: 'what-does-pet-insurance-not-cover',
    title: 'What does pet insurance not cover?',
    subtitle:
      'Every pet insurance policy has exclusions. Knowing them upfront prevents surprise denials when you file a claim.',
    metaTitle: 'What Pet Insurance Does Not Cover | GetSkimp',
    metaDescription:
      'What does pet insurance not cover? Pre-existing conditions, grooming, breeding costs, and routine care are common exclusions — see the full list here.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Pet insurance generally does not cover pre-existing conditions, routine wellness care (unless you buy a wellness add-on), grooming, breeding or pregnancy costs, cosmetic procedures, or injuries from intentional fighting or neglect. Elective procedures, experimental treatments, and costs above your annual limit are also excluded.',
        ],
      },
      {
        title: 'Common exclusions',
        bullets: [
          'Pre-existing conditions — any illness or injury with symptoms before enrollment or during waiting periods.',
          'Routine and preventive care — annual exams, vaccines, flea/tick prevention, spay/neuter (unless wellness rider).',
          'Grooming, nail trims, and boarding fees.',
          'Breeding, pregnancy, whelping, and cesarean section costs.',
          'Cosmetic procedures — tail docking, ear cropping, declawing.',
          'Behavioral training (unless specifically covered as medical treatment).',
          'Injuries from organized fighting, racing, or intentional harm.',
        ],
      },
      {
        title: 'Pre-existing conditions explained',
        paragraphs: [
          'This is the most important exclusion to understand. If your dog showed signs of limping before your policy started, a later ACL tear may be denied as related to a pre-existing orthopedic issue. Insurers review vet records from before and after enrollment to determine whether a condition existed previously.',
          'Some carriers cover "curable" pre-existing conditions after a symptom-free waiting period — often 12 months. See [[pre-existing conditions guide|/knowledge-base/pet/are-pre-existing-conditions-covered-by-pet-insurance]] for details on how insurers evaluate medical history.',
        ],
      },
      {
        title: 'Waiting period gaps',
        paragraphs: [
          'Even covered conditions are excluded during waiting periods after you enroll. Typical waiting periods are 2 to 14 days for accidents and 14 to 30 days for illnesses. Orthopedic conditions like cruciate ligament tears may have a 6- to 12-month waiting period. Any condition that appears during a waiting period is treated as pre-existing going forward.',
        ],
        numbered: [
          'Enroll before symptoms appear — ideally when your pet is young and healthy.',
          'Disclose your pet\'s full medical history accurately on the application.',
          'Keep copies of vet records in case the insurer requests them during a claim.',
          'Understand your policy\'s specific waiting period schedule before the effective date.',
        ],
      },
      {
        title: 'What about dental and wellness?',
        paragraphs: [
          'Standard accident-and-illness policies rarely cover routine dental cleanings. Accident-related dental injuries (a broken tooth from chewing a hard object) may be covered. For preventive dental and wellness care, you need a separate [[wellness plan or rider|/knowledge-base/pet/pet-insurance-vs-wellness-plans]]. Compare what each product covers before assuming your insurance policy handles annual checkups.',
        ],
      },
    ],
  },

  'accident-only-vs-accident-and-illness-pet-insurance': {
    slug: 'accident-only-vs-accident-and-illness-pet-insurance',
    title: 'Accident-only vs. accident and illness pet insurance',
    subtitle:
      'Accident-only plans cost less but leave you exposed to illness claims — which make up the majority of expensive vet bills over a pet\'s lifetime.',
    metaTitle: 'Accident-Only vs. Accident & Illness Pet Insurance | GetSkimp',
    metaDescription:
      'Compare accident-only and accident-and-illness pet insurance plans — coverage differences, typical costs, and which plan type fits your Texas pet best.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Accident-only pet insurance covers injuries from unexpected events — falls, bites, toxin ingestion, hit-by-car — but not sickness. Accident-and-illness plans cover both injuries and diseases, including cancer, infections, allergies, and chronic conditions. Accident-and-illness is the standard recommendation for most owners because illness claims are more common and often more expensive than accident claims alone.',
        ],
      },
      {
        title: 'Accident-only plans',
        bullets: [
          'Lower monthly premium — often 40% to 60% less than accident-and-illness.',
          'Covers broken bones, lacerations, foreign body surgery, and poisoning.',
          'Does not cover ear infections, cancer, allergies, diabetes, or any illness.',
          'May be the only option for very senior pets or those with significant pre-existing conditions.',
          'Best for owners on a tight budget who want some protection against emergencies.',
        ],
      },
      {
        title: 'Accident-and-illness plans',
        bullets: [
          'Comprehensive coverage for both injuries and sickness.',
          'Covers chronic conditions that develop after enrollment and waiting periods.',
          'Higher premium, but better protection against the most costly vet scenarios.',
          'Required if you want coverage for cancer, hip dysplasia, or breed-specific conditions.',
          'The plan type most Texas pet owners choose when comparing quotes.',
        ],
      },
      {
        title: 'Side-by-side comparison',
        table: {
          headers: ['Feature', 'Accident only', 'Accident & illness'],
          rows: [
            ['Avg. monthly cost (dog)', '$15–$25', '$35–$55'],
            ['Foreign body surgery', 'Covered', 'Covered'],
            ['Ear infection treatment', 'Not covered', 'Covered'],
            ['Cancer chemotherapy', 'Not covered', 'Covered'],
            ['Hip dysplasia surgery', 'Not covered', 'Covered if not pre-existing'],
            ['Best for', 'Budget-conscious owners', 'Most pet owners seeking full protection'],
          ],
        },
      },
      {
        title: 'Which should you choose?',
        paragraphs: [
          'If you can afford the difference, accident-and-illness coverage provides substantially better value. Illness-related claims — cancer, chronic kidney disease, severe allergies — are the events that most often lead to $5,000+ vet bills. Accident-only plans protect against a narrower set of risks.',
          'Accident-only may make sense for older pets who cannot qualify for illness coverage or when the illness premium exceeds what you would pay out of pocket for expected conditions. Review [[what pet insurance covers|/knowledge-base/pet/what-does-pet-insurance-cover]] and get quotes for both plan types before deciding.',
        ],
      },
    ],
  },

  'how-do-pet-insurance-deductibles-work': {
    slug: 'how-do-pet-insurance-deductibles-work',
    title: 'How do pet insurance deductibles work?',
    subtitle:
      'Your deductible is the amount you pay out of pocket before your insurer starts reimbursing eligible vet costs — and choosing the right amount affects both premium and claim payouts.',
    metaTitle: 'How Pet Insurance Deductibles Work | GetSkimp',
    metaDescription:
      'How do pet insurance deductibles work? Learn annual vs. per-incident deductibles, typical amounts, and how they affect your Texas pet insurance premium.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'A pet insurance deductible is the portion of eligible vet costs you pay before the insurer reimburses you. Most modern plans use an annual deductible — you pay it once per policy year, then the insurer covers eligible costs at your reimbursement rate for the rest of that year. Deductibles typically range from $100 to $1,000; higher deductibles mean lower monthly premiums.',
        ],
      },
      {
        title: 'Annual vs. per-incident deductibles',
        paragraphs: [
          'Annual deductibles are the industry standard today. You meet the deductible once — say $250 — and all subsequent eligible claims that year are reimbursed without another deductible. Per-incident deductibles require you to pay the deductible for each separate condition or event, which can add up quickly if your pet has multiple issues in one year.',
        ],
        table: {
          headers: ['Deductible type', 'How it works', 'Best for'],
          rows: [
            ['Annual', 'Pay once per policy year, then reimbursement applies', 'Most pet owners — simpler and often cheaper overall'],
            ['Per-incident', 'Pay deductible for each new condition or accident', 'Rare; can cost more with multiple claims per year'],
          ],
        },
      },
      {
        title: 'How deductibles interact with reimbursement',
        paragraphs: [
          'After you meet your annual deductible, the insurer reimburses eligible costs at your chosen rate — 70%, 80%, or 90%. Here is a simplified example with a $250 annual deductible and 80% reimbursement on a $2,000 eligible surgery:',
        ],
        numbered: [
          'You pay the vet $2,000 upfront and submit a claim.',
          'The insurer applies your $250 deductible, leaving $1,750 eligible for reimbursement.',
          'At 80% reimbursement, you receive $1,400 back ($1,750 × 0.80).',
          'Your total out-of-pocket cost is $600 ($250 deductible + 20% co-insurance share of $350).',
        ],
      },
      {
        title: 'Choosing the right deductible',
        bullets: [
          '$100–$250 deductible — lower out-of-pocket at claim time, but higher monthly premium. Good if you expect frequent vet visits.',
          '$500 deductible — balanced option for healthy pets with moderate emergency savings.',
          '$750–$1,000 deductible — lowest premium, but you pay more before reimbursement kicks in. Works if you have a solid emergency fund.',
          'Match your deductible to your savings — if a $500 surprise bill is manageable, a higher deductible can save on premiums.',
        ],
      },
      {
        title: 'Deductibles and Texas pricing',
        paragraphs: [
          'Moving from a $250 to a $500 deductible can reduce your monthly premium by 10% to 20% depending on the carrier and your pet\'s profile. When comparing quotes, always model a realistic claim scenario rather than focusing only on the monthly cost. See [[reimbursement levels|/knowledge-base/pet/pet-insurance-reimbursement-levels]] and [[Texas pricing guide|/knowledge-base/pet/how-much-does-pet-insurance-cost-in-texas]] to build a complete picture.',
        ],
      },
    ],
  },

  'pet-insurance-reimbursement-levels': {
    slug: 'pet-insurance-reimbursement-levels',
    title: 'Pet insurance reimbursement levels (70/80/90%)',
    subtitle:
      'Your reimbursement percentage determines how much of each eligible vet bill the insurer pays back — and directly affects your monthly premium.',
    metaTitle: 'Pet Insurance Reimbursement Levels: 70/80/90% | GetSkimp',
    metaDescription:
      'Compare 70%, 80%, and 90% pet insurance reimbursement levels — how each affects your monthly premium, claim payouts, and out-of-pocket costs in Texas.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Pet insurance reimbursement levels — typically 70%, 80%, or 90% — define what share of eligible vet costs the insurer pays after your deductible is met. A 90% plan means you pay 10% of eligible costs as co-insurance; a 70% plan means you pay 30%. Higher reimbursement rates come with higher monthly premiums but lower out-of-pocket costs when you file a claim.',
        ],
      },
      {
        title: 'How each level works',
        table: {
          headers: ['Reimbursement', 'Insurer pays', 'You pay (co-insurance)', 'Typical premium impact'],
          rows: [
            ['70%', '70% of eligible costs', '30%', 'Lowest monthly premium'],
            ['80%', '80% of eligible costs', '20%', 'Mid-range premium — most popular choice'],
            ['90%', '90% of eligible costs', '10%', 'Highest monthly premium'],
          ],
        },
      },
      {
        title: 'Example: $3,000 eligible claim',
        paragraphs: [
          'Assume you have a $250 annual deductible already met and a $3,000 eligible vet bill. Here is what you would receive at each reimbursement level:',
        ],
        bullets: [
          '70% reimbursement — insurer pays $2,100; your co-insurance share is $900.',
          '80% reimbursement — insurer pays $2,400; your co-insurance share is $600.',
          '90% reimbursement — insurer pays $2,700; your co-insurance share is $300.',
        ],
      },
      {
        title: 'Which reimbursement level should you choose?',
        paragraphs: [
          '80% is the most common choice among Texas pet owners because it balances affordable premiums with reasonable claim payouts. Choose 90% if you want maximum protection and do not mind paying $10 to $20 more per month. Choose 70% if your primary goal is keeping the monthly premium as low as possible and you have savings to cover a larger co-insurance share when claims arise.',
        ],
        bullets: [
          '90% — best for breeds prone to expensive conditions or owners with limited emergency savings.',
          '80% — sweet spot for most dogs and cats; good balance of cost and coverage.',
          '70% — budget option when you can absorb 30% of large bills out of pocket.',
        ],
      },
      {
        title: 'Reimbursement is not the only factor',
        paragraphs: [
          'Your total out-of-pocket cost depends on the combination of deductible, reimbursement rate, and annual limit — not any single setting. A 90% plan with a $1,000 deductible may cost you more at claim time than an 80% plan with a $250 deductible on moderate bills. Use our [[deductible guide|/knowledge-base/pet/how-do-pet-insurance-deductibles-work]] and [[Texas cost guide|/knowledge-base/pet/how-much-does-pet-insurance-cost-in-texas]] to compare full scenarios before enrolling.',
        ],
      },
    ],
  },

  'are-pre-existing-conditions-covered-by-pet-insurance': {
    slug: 'are-pre-existing-conditions-covered-by-pet-insurance',
    title: 'Are pre-existing conditions covered by pet insurance?',
    subtitle:
      'Pre-existing conditions are the most common reason pet insurance claims are denied — and why enrolling before symptoms appear matters.',
    metaTitle: 'Pre-Existing Conditions & Pet Insurance | GetSkimp',
    metaDescription:
      'Are pre-existing conditions covered by pet insurance? Learn how insurers define them, curable vs. incurable rules, and why early enrollment in Texas matters.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'No — pet insurance does not cover pre-existing conditions. A pre-existing condition is any illness or injury that showed symptoms, was diagnosed, or was treated before your policy effective date or during a waiting period. This is standard across virtually all pet insurers. The best strategy is to enroll your pet while they are healthy, before any conditions develop.',
        ],
      },
      {
        title: 'How insurers define pre-existing conditions',
        paragraphs: [
          'Insurers review your pet\'s medical records from before enrollment to identify pre-existing conditions. A condition is considered pre-existing even if it was never formally diagnosed — if your vet noted symptoms like limping, coughing, or skin irritation in prior records, a related claim may be denied later.',
        ],
        bullets: [
          'Incurable pre-existing — permanent exclusions (e.g., diabetes diagnosed before enrollment).',
          'Curable pre-existing — some insurers cover after 12 months with no symptoms, treatment, or medication.',
          'Bilateral conditions — if one knee had a pre-existing issue, the other knee may also be excluded.',
          'Related conditions — an insurer may deny claims for conditions they consider connected to a pre-existing issue.',
        ],
      },
      {
        title: 'Why timing matters',
        paragraphs: [
          'Every month you wait to enroll is a month your pet could develop a condition that becomes permanently excluded. A puppy who develops allergies at 8 months will have those allergies excluded if you wait until 9 months to buy insurance. This is why [[pet insurance for puppies and kittens|/knowledge-base/pet/pet-insurance-for-puppies-and-kittens]] is strongly recommended.',
          'Waiting periods create a second window where conditions can become pre-existing. If your cat develops a urinary infection during the 14-day illness waiting period, that condition and related future claims may be excluded permanently.',
        ],
      },
      {
        title: 'What you can do',
        numbered: [
          'Enroll as early as possible — ideally when you first get your pet or at the first sign of a healthy checkup.',
          'Be honest on your application; misrepresentation can void your policy.',
          'Keep a file of vet records so you can show when conditions first appeared if disputes arise.',
          'Ask your insurer about their curable pre-existing condition policy before buying.',
          'If your pet already has conditions, understand that [[what insurance does not cover|/knowledge-base/pet/what-does-pet-insurance-not-cover]] still leaves other illnesses and accidents eligible.',
        ],
      },
      {
        title: 'Pre-existing vs. hereditary conditions',
        paragraphs: [
          'Pre-existing and hereditary are different concepts. A hereditary condition like hip dysplasia is not automatically pre-existing — if your dog shows no symptoms before enrollment and the waiting period passes, it may be covered. However, some carriers exclude specific hereditary conditions for certain breeds regardless of timing. Read your policy\'s breed-specific exclusions carefully.',
        ],
      },
    ],
  },

  'pet-insurance-for-puppies-and-kittens': {
    slug: 'pet-insurance-for-puppies-and-kittens',
    title: 'Pet insurance for puppies and kittens',
    subtitle:
      'Enrolling a puppy or kitten early locks in lower premiums and ensures future illnesses are not excluded as pre-existing conditions.',
    metaTitle: 'Pet Insurance for Puppies & Kittens | GetSkimp',
    metaDescription:
      'Should you get pet insurance for a puppy or kitten? Learn why early enrollment in Texas saves money and protects against pre-existing condition exclusions.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Insuring a puppy or kitten as early as 8 weeks old is one of the best financial decisions you can make as a pet owner. Premiums are at their lowest, pre-existing condition exclusions have not yet accumulated, and any illness that develops after waiting periods pass will be covered. Most carriers allow enrollment starting at 6 to 8 weeks of age.',
        ],
      },
      {
        title: 'Why enroll young',
        bullets: [
          'Lowest premiums — a puppy policy may cost $25 to $40 per month vs. $60+ for the same dog at age 8.',
          'No pre-existing exclusions yet — conditions that develop after enrollment are covered (after waiting periods).',
          'Covers common puppy/kitten issues — parvovirus, upper respiratory infections, foreign body ingestion.',
          'Protects against future chronic conditions — allergies, hip dysplasia, and heart disease if they arise later.',
          'Some carriers waive exam fee requirements for pets under 1 year old.',
        ],
      },
      {
        title: 'What to watch for with young pets',
        paragraphs: [
          'Puppies and kittens are curious and accident-prone. Foreign body ingestion (swallowing toys, socks, or rocks) is one of the most common emergency claims for dogs under 2 years old. Kittens may injure themselves climbing or fall from heights. Accident-and-illness coverage handles both scenarios after the short waiting period passes.',
          'Vaccinations and spay/neuter surgery are not covered by standard insurance — those fall under [[wellness plans|/knowledge-base/pet/pet-insurance-vs-wellness-plans]]. Budget separately for routine puppy/kitten vet visits during the first year, which typically run $300 to $800.',
        ],
      },
      {
        title: 'Recommended coverage settings for young pets',
        table: {
          headers: ['Setting', 'Recommendation', 'Why'],
          rows: [
            ['Plan type', 'Accident & illness', 'Covers both injuries and sickness from the start'],
            ['Deductible', '$250–$500', 'Balances premium savings with manageable out-of-pocket'],
            ['Reimbursement', '80% or 90%', 'Higher reimbursement protects against surprise large bills'],
            ['Annual limit', '$10,000+ or unlimited', 'Young pets have decades of potential claims ahead'],
          ],
        },
      },
      {
        title: 'When to enroll after adoption',
        numbered: [
          'Schedule a vet exam within the first week of adoption to establish a clean health baseline.',
          'Enroll before or immediately after the first vet visit — do not wait for a problem to appear.',
          'Understand waiting periods: typically 2–14 days for accidents, 14–30 days for illnesses.',
          'Disclose any conditions noted by the shelter or breeder on your application.',
          'Review [[pre-existing condition rules|/knowledge-base/pet/are-pre-existing-conditions-covered-by-pet-insurance]] so you know what is at stake if you delay.',
        ],
      },
    ],
  },

  'pet-insurance-for-senior-pets': {
    slug: 'pet-insurance-for-senior-pets',
    title: 'Pet insurance for senior pets',
    subtitle:
      'Insuring an older dog or cat is still possible, but premiums are higher and pre-existing exclusions may limit what the policy pays.',
    metaTitle: 'Pet Insurance for Senior Dogs & Cats | GetSkimp',
    metaDescription:
      'Can you get pet insurance for a senior pet? Learn about age limits, premium costs, coverage options, and whether insuring an older dog or cat is worth it.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'You can insure senior pets — generally dogs and cats age 7 to 14 and older — but premiums are significantly higher and any conditions they already have will be excluded. If your senior pet is still relatively healthy with no major diagnosed conditions, insurance can still protect against new illnesses and accidents. If they already have chronic conditions, the value of a new policy drops sharply.',
        ],
      },
      {
        title: 'Challenges with senior pet insurance',
        bullets: [
          'Higher premiums — expect $60 to $100+ per month for a senior dog in Texas.',
          'Pre-existing exclusions — arthritis, heart murmurs, or kidney issues already on vet records will not be covered.',
          'Age limits — some carriers stop offering new policies after age 14 for dogs or 12 for cats.',
          'Reduced plan options — accident-only may be the only choice for pets with significant health history.',
          'Premium increases — rates typically rise each year as your pet ages, even without claims.',
        ],
      },
      {
        title: 'When senior pet insurance still makes sense',
        paragraphs: [
          'If your 8-year-old dog has a clean medical history and no chronic diagnoses, enrolling now can still cover cancer, new orthopedic injuries, and unexpected illnesses for years to come. The math works when a single major claim — cancer treatment can exceed $10,000 — would exceed several years of premiums.',
          'For a 12-year-old cat with controlled hyperthyroidism, a new policy would exclude the thyroid condition but could still cover unrelated events like a broken bone or urinary blockage. Evaluate whether the premium justifies coverage for only new, unrelated conditions.',
        ],
      },
      {
        title: 'Tips for insuring older pets',
        numbered: [
          'Get quotes from multiple carriers — age cutoff and pricing vary significantly.',
          'Request a medical record review before enrolling to understand what will be excluded.',
          'Consider accident-only if illness premiums are prohibitive or pre-existing conditions dominate.',
          'Choose a lower deductible if you expect more frequent vet visits in the coming years.',
          'Compare the cost against [[self-insuring|/knowledge-base/pet/is-pet-insurance-worth-it]] with a dedicated pet emergency fund.',
        ],
      },
      {
        title: 'Senior pets and dental care',
        paragraphs: [
          'Dental disease is extremely common in senior pets and can lead to costly extractions and infections. Standard insurance rarely covers routine dental cleanings, though accident-related dental injuries may qualify. Read [[does pet insurance cover dental|/knowledge-base/pet/does-pet-insurance-cover-dental]] and consider a wellness add-on if dental care is a priority for your older pet.',
        ],
      },
    ],
  },

  'how-to-file-a-pet-insurance-claim': {
    slug: 'how-to-file-a-pet-insurance-claim',
    title: 'How to file a pet insurance claim',
    subtitle:
      'Filing a pet insurance claim is straightforward — pay the vet, gather your documents, and submit through your insurer\'s portal or app.',
    metaTitle: 'How to File a Pet Insurance Claim | GetSkimp',
    metaDescription:
      'How to file a pet insurance claim step by step — documents you need, typical processing times, and tips to get vet bill reimbursement faster in Texas.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'To file a pet insurance claim, pay your veterinarian at the time of service, then submit an itemized invoice and medical records to your insurer through their website, app, or email. Most insurers process claims within 5 to 15 business days and deposit reimbursement directly to your bank account. You can file claims for any licensed vet — there is no network restriction.',
        ],
      },
      {
        title: 'Step-by-step claims process',
        numbered: [
          'Visit any licensed veterinarian and pay the full bill at checkout.',
          'Request an itemized invoice showing dates of service, diagnosis codes, and individual charges.',
          'Ask your vet for copies of exam notes, lab results, or surgical reports related to the visit.',
          'Log in to your insurer\'s portal or app and start a new claim.',
          'Upload the invoice, medical records, and any supporting documents.',
          'Wait for the insurer to review — they may request additional records.',
          'Receive reimbursement via direct deposit or check minus your deductible and co-insurance share.',
        ],
      },
      {
        title: 'Documents you will need',
        bullets: [
          'Itemized invoice with pet name, date of service, and line-item charges.',
          'Medical records or vet notes describing the diagnosis and treatment.',
          'Lab results, X-ray reports, or surgical summaries for complex claims.',
          'Proof of payment (receipt showing the bill was paid in full).',
          'Your policy number and pet identification details.',
        ],
      },
      {
        title: 'Tips for faster reimbursement',
        bullets: [
          'Submit claims promptly — some insurers have time limits (60 to 180 days from date of service).',
          'Make sure the invoice includes diagnosis codes (ICD codes) — claims without them may be delayed.',
          'Keep a digital folder of all vet records so you can attach them quickly.',
          'Use your insurer\'s mobile app for photo uploads right from the vet office.',
          'Follow up if you have not heard back within the insurer\'s stated processing window.',
        ],
      },
      {
        title: 'What to do if a claim is denied',
        paragraphs: [
          'Common denial reasons include pre-existing conditions, services during waiting periods, non-covered procedures (grooming, wellness), and incomplete documentation. If your claim is denied, request a written explanation citing the specific policy provision. You can appeal with additional medical records, especially if you believe the condition is not related to a pre-existing issue.',
          'Understanding [[what is and is not covered|/knowledge-base/pet/what-does-pet-insurance-cover]] before you enroll reduces surprises at claim time. Keep your [[deductible|/knowledge-base/pet/how-do-pet-insurance-deductibles-work]] and [[reimbursement level|/knowledge-base/pet/pet-insurance-reimbursement-levels]] in mind when estimating your expected payout.',
        ],
      },
    ],
  },

  'does-pet-insurance-cover-dental': {
    slug: 'does-pet-insurance-cover-dental',
    title: 'Does pet insurance cover dental?',
    subtitle:
      'Dental coverage in pet insurance is limited — accident-related tooth injuries are often covered, but routine cleanings usually require a wellness add-on.',
    metaTitle: 'Does Pet Insurance Cover Dental? | GetSkimp',
    metaDescription:
      'Does pet insurance cover dental cleanings and extractions? Learn what dental care is covered, what is excluded, and when a wellness plan add-on makes sense.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Standard accident-and-illness pet insurance covers dental injuries caused by accidents — such as a broken tooth from chewing a hard object or mouth trauma from a fall. It generally does not cover routine dental cleanings, periodontal disease treatment, or extractions related to pre-existing dental conditions. For preventive dental care, you need a wellness plan or rider.',
        ],
      },
      {
        title: 'What dental care is typically covered',
        bullets: [
          'Broken, chipped, or fractured teeth from accidents (not from chewing or wear).',
          'Mouth and jaw injuries from falls, fights, or trauma.',
          'Tooth extraction when directly caused by an covered accident.',
          'Dental X-rays and anesthesia tied to an accident-related dental procedure.',
        ],
      },
      {
        title: 'What dental care is usually excluded',
        bullets: [
          'Routine dental cleanings and prophylaxis (preventive scaling and polishing).',
          'Periodontal disease and gingivitis treatment.',
          'Extractions due to advanced dental decay or untreated gum disease.',
          'Pre-existing dental conditions noted before enrollment.',
          'Cosmetic dental work.',
        ],
      },
      {
        title: 'Why dental matters for pet owners',
        paragraphs: [
          'Dental disease affects the majority of dogs and cats by age 3. Advanced periodontal disease can cost $500 to $3,000 for extractions, X-rays, and anesthesia — especially in [[senior pets|/knowledge-base/pet/pet-insurance-for-senior-pets]]. Because standard insurance excludes routine dental, many owners add a [[wellness plan|/knowledge-base/pet/pet-insurance-vs-wellness-plans]] that includes an annual cleaning allowance.',
          'Some carriers offer dental illness riders on accident-and-illness policies that cover non-accident dental treatment after a waiting period. These are less common and may have sub-limits of $500 to $1,500 per year. Ask about dental riders when you [[choose a plan in Texas|/knowledge-base/pet/how-to-choose-pet-insurance-in-texas]].',
        ],
      },
      {
        title: 'Dental coverage comparison',
        table: {
          headers: ['Procedure', 'Standard insurance', 'Wellness add-on'],
          rows: [
            ['Broken tooth from accident', 'Usually covered', 'N/A'],
            ['Annual dental cleaning', 'Not covered', 'Often covered up to a limit'],
            ['Periodontal extractions', 'Not covered', 'Sometimes partially covered'],
            ['Dental X-rays (routine)', 'Not covered', 'May be included in cleaning benefit'],
          ],
        },
      },
    ],
  },

  'pet-insurance-vs-wellness-plans': {
    slug: 'pet-insurance-vs-wellness-plans',
    title: 'Pet insurance vs. wellness plans',
    subtitle:
      'Pet insurance protects against expensive emergencies and illnesses; wellness plans help budget for routine preventive care. They solve different problems.',
    metaTitle: 'Pet Insurance vs. Wellness Plans | GetSkimp',
    metaDescription:
      'Pet insurance vs. wellness plans — compare accident-and-illness coverage with preventive care plans for vaccines, exams, and dental cleanings in Texas.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Pet insurance (accident-and-illness) reimburses you for unexpected veterinary costs like surgery, hospitalization, and disease treatment. Wellness plans reimburse predictable routine care like annual exams, vaccinations, flea prevention, and dental cleanings. They are separate products — wellness plans do not cover emergencies, and standard insurance does not cover routine care.',
        ],
      },
      {
        title: 'Pet insurance (accident and illness)',
        bullets: [
          'Covers unexpected injuries and illnesses after waiting periods.',
          'Protects against large bills — surgery, cancer treatment, emergency visits.',
          'Monthly cost: $25 to $70+ depending on pet and plan design.',
          'Reimbursement model — pay vet first, then file a claim.',
          'Essential financial protection for most pet owners.',
        ],
      },
      {
        title: 'Wellness plans',
        bullets: [
          'Covers routine and preventive care on a predictable schedule.',
          'Typical benefits: annual exam, vaccines, heartworm test, fecal exam, dental cleaning allowance.',
          'Monthly cost: $20 to $50 as a rider, or bundled with insurance.',
          'Not a substitute for insurance — no coverage for emergencies or major illness.',
          'Useful for budgeting known annual vet costs, not catastrophic events.',
        ],
      },
      {
        title: 'Side-by-side comparison',
        table: {
          headers: ['Feature', 'Pet insurance', 'Wellness plan'],
          rows: [
            ['Emergency surgery', 'Covered', 'Not covered'],
            ['Annual vaccinations', 'Not covered', 'Covered'],
            ['Cancer treatment', 'Covered', 'Not covered'],
            ['Dental cleaning', 'Not covered (usually)', 'Often covered up to a limit'],
            ['Spay/neuter', 'Not covered', 'Sometimes covered'],
            ['Best for', 'Unexpected major vet bills', 'Predictable preventive care costs'],
          ],
        },
      },
      {
        title: 'Should you get both?',
        paragraphs: [
          'Many Texas pet owners choose accident-and-illness insurance as the priority and add a wellness rider if they want help budgeting for annual care. A wellness plan alone leaves you fully exposed to the $3,000+ emergencies that insurance is designed to handle. If budget is tight, buy insurance first and pay for wellness out of pocket.',
          'When comparing options, review [[what insurance covers|/knowledge-base/pet/what-does-pet-insurance-cover]], [[dental coverage|/knowledge-base/pet/does-pet-insurance-cover-dental]], and [[Texas pricing|/knowledge-base/pet/how-much-does-pet-insurance-cost-in-texas]] to understand the full cost picture before adding a wellness component.',
        ],
      },
    ],
  },

  'how-to-choose-pet-insurance-in-texas': {
    slug: 'how-to-choose-pet-insurance-in-texas',
    title: 'How to choose pet insurance in Texas',
    subtitle:
      'Compare carriers, coverage limits, and plan features side by side to find pet insurance that fits your budget and your pet\'s health needs.',
    metaTitle: 'How to Choose Pet Insurance in Texas | GetSkimp',
    metaDescription:
      'How to choose pet insurance in Texas — compare carriers, deductibles, reimbursement rates, and coverage limits to find the best plan for your dog or cat.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'To choose pet insurance in Texas, compare at least three carriers on plan type (accident-and-illness vs. accident-only), annual limits, deductibles, reimbursement rates, waiting periods, and pre-existing condition policies. Match the plan to your pet\'s age, breed, and health history — and prioritize comprehensive coverage over the lowest monthly premium when possible.',
        ],
      },
      {
        title: 'Step-by-step comparison guide',
        numbered: [
          'Gather your pet\'s info — species, breed, age, weight, spay/neuter status, and zip code.',
          'Get quotes from multiple carriers for accident-and-illness coverage.',
          'Compare annual limits — $10,000 minimum recommended; unlimited is ideal for breeds prone to expensive conditions.',
          'Evaluate deductibles ($250–$500 is a common sweet spot) and reimbursement (80% is popular).',
          'Read waiting period schedules — especially for orthopedic and dental conditions.',
          'Review pre-existing condition and breed-specific exclusion policies.',
          'Check customer reviews for claims processing speed and denial rates.',
          'Enroll while your pet is healthy to avoid new exclusions.',
        ],
      },
      {
        title: 'Key features to compare',
        table: {
          headers: ['Feature', 'What to look for'],
          rows: [
            ['Plan type', 'Accident-and-illness for comprehensive protection'],
            ['Annual limit', '$10,000+ or unlimited'],
            ['Deductible', '$250–$500 annual for most pets'],
            ['Reimbursement', '80% or 90% for better claim payouts'],
            ['Waiting periods', 'Shorter is better; note orthopedic waiting periods'],
            ['Multi-pet discount', '5–10% off for insuring multiple pets'],
            ['Direct vet pay', 'Some carriers offer direct payment to the vet (optional convenience)'],
          ],
        },
      },
      {
        title: 'Texas-specific considerations',
        bullets: [
          'Heat-related emergencies — heatstroke and paw pad burns are more common in Texas summers; ensure accident coverage is robust.',
          'Vector-borne diseases — heartworm, tick-borne illnesses like ehrlichiosis are prevalent; illness coverage handles treatment.',
          'Vet cost variation — urban areas (Austin, Dallas, Houston) have higher vet costs than rural Texas, which affects premiums.',
          'No state mandate — Texas does not require pet insurance, so coverage is entirely your choice.',
          'Any licensed vet — Texas pet insurance has no network restrictions; visit any vet in the state or country.',
        ],
      },
      {
        title: 'Common mistakes to avoid',
        bullets: [
          'Choosing accident-only to save money when illness coverage is affordable.',
          'Waiting until your pet is sick to enroll — [[pre-existing conditions|/knowledge-base/pet/are-pre-existing-conditions-covered-by-pet-insurance]] will be excluded.',
          'Focusing only on monthly premium without modeling a realistic claim scenario.',
          'Selecting a low annual limit ($2,500–$5,000) that a single surgery could exhaust.',
          'Skipping the policy fine print on waiting periods and breed exclusions.',
        ],
      },
    ],
  },

  'how-do-pet-insurance-waiting-periods-work': {
    slug: 'how-do-pet-insurance-waiting-periods-work',
    title: 'How do pet insurance waiting periods work?',
    subtitle:
      'Waiting periods are the days after enrollment before your policy pays for accidents, illnesses, or specific conditions — and any problem that appears during that window may be excluded permanently.',
    metaTitle: 'Pet Insurance Waiting Periods Explained | GetSkimp',
    metaDescription:
      'How do pet insurance waiting periods work? Learn accident, illness, and orthopedic waiting rules and why enrolling before symptoms appear matters in Texas.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'A waiting period is the number of days after your policy starts (or after you add coverage) before the insurer will pay claims for a given type of condition. Most policies have a short waiting period for accidents (often 2–14 days), a longer one for illnesses (often 14–30 days), and sometimes a separate extended waiting period for orthopedic issues such as cruciate ligament tears or hip dysplasia (6–12 months). Any illness or injury with symptoms during a waiting period is usually treated as pre-existing and excluded going forward.',
        ],
      },
      {
        title: 'Types of waiting periods',
        table: {
          headers: ['Coverage type', 'Typical waiting period', 'What happens if symptoms appear early'],
          rows: [
            ['Accidents', '2–14 days', 'Accident claims denied; condition may be excluded as pre-existing'],
            ['Illnesses', '14–30 days', 'Illness and related future claims often permanently excluded'],
            ['Orthopedic / cruciate', '6–12 months on some plans', 'Knee, hip, and ligament issues excluded if signs appear during wait'],
            ['Dental illness (if offered)', '14–30 days or longer', 'Non-accident dental excluded until period ends'],
          ],
        },
      },
      {
        title: 'Why waiting periods exist',
        paragraphs: [
          'Insurers use waiting periods so owners cannot buy coverage only after a vet visit or diagnosis is already planned. Without them, someone could enroll the day before surgery and file a large claim immediately. Waiting periods protect the risk pool and keep policies sustainable for owners who enroll while pets are healthy.',
          'This is why [[enrolling puppies and kittens early|/knowledge-base/pet/pet-insurance-for-puppies-and-kittens]] and avoiding gaps in coverage matter. If you cancel and re-enroll later, waiting periods typically restart and new conditions can become excluded. See [[pre-existing condition rules|/knowledge-base/pet/are-pre-existing-conditions-covered-by-pet-insurance]] for how early symptoms affect future claims.',
        ],
      },
      {
        title: 'How to plan around waiting periods',
        numbered: [
          'Enroll as soon as your pet is eligible — often 6–8 weeks of age for puppies and kittens.',
          'Read your policy\'s exact schedule for accidents, illnesses, and orthopedic conditions before the effective date.',
          'Avoid delaying treatment for new symptoms, but understand that care during a waiting period may not be reimbursed.',
          'Keep vet records showing when symptoms first appeared if you later dispute a pre-existing denial.',
          'If you move or travel, waiting periods do not reset — see [[out-of-state coverage|/knowledge-base/pet/does-pet-insurance-work-out-of-state]].',
        ],
      },
      {
        title: 'Waiting periods vs. exclusions',
        paragraphs: [
          'Waiting periods are temporary gaps at the start of coverage. Exclusions — such as [[breed-specific restrictions|/knowledge-base/pet/pet-insurance-breed-exclusions-and-restrictions]] or [[hereditary condition limits|/knowledge-base/pet/does-pet-insurance-cover-hereditary-conditions]] — can apply for the life of the policy regardless of waiting periods. Review both sections of your policy documents before you rely on coverage for expensive treatments like [[hip dysplasia|/knowledge-base/pet/does-pet-insurance-cover-hip-dysplasia]] or [[cancer care|/knowledge-base/pet/does-pet-insurance-cover-cancer]].',
        ],
      },
    ],
  },

  'does-pet-insurance-work-out-of-state': {
    slug: 'does-pet-insurance-work-out-of-state',
    title: 'Does pet insurance work out of state?',
    subtitle:
      'Most U.S. pet insurance policies reimburse care from any licensed veterinarian nationwide — so travel, relocation, and snowbird trips are usually covered the same way as home vet visits.',
    metaTitle: 'Does Pet Insurance Work Out of State? | GetSkimp',
    metaDescription:
      'Does pet insurance work out of state? Learn how nationwide vet reimbursement works when you travel, relocate, or visit emergency clinics away from home.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Yes — for most major U.S. pet insurers, your policy works out of state. Pet insurance is reimbursement-based, not network-based like human HMO plans. You can take your dog or cat to any licensed veterinarian in the United States (and often Canada), pay the bill, and file a claim the same way you would at your regular Texas vet. Your premium and policy terms are generally tied to where you lived when you enrolled, not where the vet visit happens.',
        ],
      },
      {
        title: 'How out-of-state vet visits work',
        numbered: [
          'Visit any licensed vet in the U.S. (confirm your policy for Canada or international travel).',
          'Pay the full invoice at the time of service — insurers rarely bill the vet directly.',
          'Collect an itemized invoice and medical notes, as you would for a home claim.',
          'Submit the claim through your insurer\'s app or portal.',
          'Receive reimbursement for eligible costs per your deductible, reimbursement rate, and [[annual limit|/knowledge-base/pet/annual-coverage-limits-in-pet-insurance]].',
        ],
      },
      {
        title: 'Travel and relocation scenarios',
        bullets: [
          'Vacation emergencies — coverage applies if waiting periods are satisfied and the condition is not pre-existing.',
          'Moving to another state — you usually keep the same policy; notify your insurer of address changes.',
          'Snowbirds and extended stays — no special rider needed for temporary out-of-state care on most plans.',
          'Specialist or university vet hospitals — covered if licensed and treatment is eligible under your policy.',
          '[[Emergency vet visits|/knowledge-base/pet/does-pet-insurance-cover-emergency-vet-visits]] — after-hours and ER clinics qualify the same in any state.',
        ],
      },
      {
        title: 'Exceptions and fine print',
        paragraphs: [
          'Some policies exclude care outside the U.S. or limit international travel. Military deployments, overseas moves, or border-area travel may require confirming coverage territory with your carrier. If you permanently relocate, your insurer may adjust future renewal terms based on your new zip code\'s vet cost index — that affects renewal, not whether a one-time out-of-state claim during travel is allowed.',
          'Telehealth consultations alone are often not reimbursable unless tied to an eligible in-person treatment. Prescriptions filled out of state are generally fine if the medication treats a covered condition — see [[prescription medication coverage|/knowledge-base/pet/does-pet-insurance-cover-prescription-medications]].',
        ],
      },
      {
        title: 'Texas owners who travel often',
        paragraphs: [
          'Heat-related emergencies, tick exposure, and travel stress can trigger vet visits far from home. Accident-and-illness coverage applies nationwide as long as the event is not excluded. Before a long trip, confirm your insurer\'s claim submission deadlines and keep digital copies of [[how to file a claim|/knowledge-base/pet/how-to-file-a-pet-insurance-claim]] instructions on your phone. If you insure multiple pets, each policy typically follows the same nationwide rule — see [[multi-pet coverage|/knowledge-base/pet/multi-pet-insurance-coverage]].',
        ],
      },
    ],
  },

  'annual-coverage-limits-in-pet-insurance': {
    slug: 'annual-coverage-limits-in-pet-insurance',
    title: 'Annual coverage limits in pet insurance',
    subtitle:
      'Your annual coverage limit is the maximum amount your insurer will reimburse in a 12-month policy period — and choosing the right cap matters when claims run into the thousands.',
    metaTitle: 'Annual Coverage Limits in Pet Insurance | GetSkimp',
    metaDescription:
      'What are annual coverage limits in pet insurance? Compare caps from $5K to unlimited, per-condition limits, and how limits affect big vet claims in Texas.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'An annual coverage limit is the most your pet insurance policy will pay toward eligible claims in one policy year. Common caps include $5,000, $10,000, $15,000, and unlimited. Once you hit the limit, you pay remaining vet costs out of pocket until the policy year resets. Higher limits provide more protection for serious conditions like [[cancer|/knowledge-base/pet/does-pet-insurance-cover-cancer]] or multiple [[emergency visits|/knowledge-base/pet/does-pet-insurance-cover-emergency-vet-visits]] in the same year.',
        ],
      },
      {
        title: 'Types of limits',
        table: {
          headers: ['Limit type', 'How it works', 'Consideration'],
          rows: [
            ['Annual (most common)', 'Resets each policy year; all eligible claims count toward one pool', 'Best for straightforward budgeting of max reimbursement'],
            ['Per-condition / per-incident', 'Cap applies separately to each diagnosis or event', 'A single condition may exhaust its cap while others remain available'],
            ['Lifetime', 'Total reimbursement cap over the pet\'s entire insured life', 'Less common today; can run out for chronic pets'],
            ['Unlimited annual', 'No annual cap on eligible reimbursement', 'Strongest protection for high-cost multi-year illnesses'],
          ],
        },
      },
      {
        title: 'How limits interact with other plan features',
        paragraphs: [
          'Your limit works alongside your [[deductible|/knowledge-base/pet/how-do-pet-insurance-deductibles-work]] and [[reimbursement percentage|/knowledge-base/pet/pet-insurance-reimbursement-levels]]. Only eligible costs count toward the cap after exclusions and co-insurance are applied. For example, on an 80% plan with a $10,000 annual limit, the insurer might reimburse up to $10,000 in a year — not $10,000 in vet bills before co-insurance (always read your policy\'s definition).',
          'Some policies also have sub-limits for specific benefits — behavioral therapy, alternative care, or exam fees — even when the overall annual limit is higher. Check the benefit schedule, not just the headline limit number.',
        ],
      },
      {
        title: 'Choosing the right annual limit',
        bullets: [
          '$5,000 — may cover one moderate surgery but can be exhausted by a single cancer treatment year.',
          '$10,000 — reasonable floor for many adult dogs and cats; handles several serious events in one year.',
          '$15,000+ — better for breeds prone to orthopedic or chronic disease.',
          'Unlimited — strongest option when you want no cap on eligible reimbursement over time.',
          'Match limit to risk — [[hip dysplasia|/knowledge-base/pet/does-pet-insurance-cover-hip-dysplasia]] surgery alone can approach $5,000–$8,000.',
        ],
      },
      {
        title: 'What happens when you hit the limit',
        paragraphs: [
          'After the annual maximum is reached, you pay 100% of additional eligible-type care until your policy year renews. Chronic conditions spanning multiple years reset the annual pool each renewal period on standard plans — but [[pre-existing conditions|/knowledge-base/pet/are-pre-existing-conditions-covered-by-pet-insurance]] remain excluded. When comparing carriers, model a worst-case year (surgery plus follow-up care) against your limit. See [[how to choose pet insurance in Texas|/knowledge-base/pet/how-to-choose-pet-insurance-in-texas]] for a full comparison checklist.',
        ],
      },
    ],
  },

  'multi-pet-insurance-coverage': {
    slug: 'multi-pet-insurance-coverage',
    title: 'Multi-pet insurance coverage',
    subtitle:
      'Insuring two or more dogs or cats usually means separate policies per pet — with optional household discounts, independent deductibles, and the same nationwide vet rules for each animal.',
    metaTitle: 'Multi-Pet Insurance Coverage Guide | GetSkimp',
    metaDescription:
      'How does multi-pet insurance work? Learn separate policies, per-pet deductibles, household discounts, and what to compare when insuring multiple dogs or cats.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Multi-pet insurance typically means enrolling each dog or cat on their own policy under the same account or carrier. Each pet has their own premium, deductible, reimbursement rate, [[annual limit|/knowledge-base/pet/annual-coverage-limits-in-pet-insurance]], and waiting periods. Many carriers offer a multi-pet discount — often 5% to 10% — on additional pets insured on the same plan. Coverage does not pool between pets; one pet\'s claims do not reduce another\'s limit.',
        ],
      },
      {
        title: 'How multi-pet enrollment works',
        numbered: [
          'Get a quote for each pet using species, breed, age, and health history.',
          'Choose plan settings per pet — you can use different deductibles or limits for a senior cat vs. a young dog.',
          'Apply multi-pet discount at checkout if the carrier offers it on the same account.',
          'Complete separate waiting periods for each pet based on their policy start date.',
          'File claims individually with invoices tied to the correct pet.',
        ],
      },
      {
        title: 'Separate policies vs. shared benefits',
        table: {
          headers: ['Feature', 'Typical multi-pet setup'],
          rows: [
            ['Deductible', 'Each pet has their own annual deductible'],
            ['Annual limit', 'Each pet has their own cap or unlimited pool'],
            ['Pre-existing conditions', 'Evaluated per pet\'s medical records'],
            ['Waiting periods', 'Start independently per enrollment date'],
            ['Discount', 'Often applied to 2nd+ pet premium on same account'],
            ['Claims', 'Submitted per pet; no sharing of unused limit'],
          ],
        },
      },
      {
        title: 'Tips for households with multiple pets',
        bullets: [
          'Enroll younger pets first — lower starting rates and fewer exclusions before conditions develop.',
          'Align plan types — accident-and-illness for all pets unless one only qualifies for accident-only.',
          'Track each pet\'s [[waiting periods|/knowledge-base/pet/how-do-pet-insurance-waiting-periods-work]] separately after adoption dates differ.',
          'Review [[breed exclusions|/knowledge-base/pet/pet-insurance-breed-exclusions-and-restrictions]] per pet if breeds differ widely.',
          'Use the same carrier for simpler billing, or compare multiple carriers if one pet has special health needs.',
        ],
      },
      {
        title: 'When insuring multiple pets is worth it',
        paragraphs: [
          'Two pets mean twice the chance of an unexpected vet bill in the same year. A dog who swallows a foreign body and a cat who develops a urinary blockage could both need [[emergency care|/knowledge-base/pet/does-pet-insurance-cover-emergency-vet-visits]] in one calendar year. Separate policies keep each pet\'s protection independent. Compare total household value using [[what pet insurance covers|/knowledge-base/pet/what-does-pet-insurance-cover]] and [[how to choose a plan|/knowledge-base/pet/how-to-choose-pet-insurance-in-texas]] rather than assuming a multi-pet discount alone makes a weak plan sufficient.',
        ],
      },
    ],
  },

  'does-pet-insurance-cover-cancer': {
    slug: 'does-pet-insurance-cover-cancer',
    title: 'Does pet insurance cover cancer?',
    subtitle:
      'Accident-and-illness pet insurance typically covers cancer diagnosis and treatment — chemotherapy, surgery, and hospitalization — if the disease is not pre-existing and waiting periods have passed.',
    metaTitle: 'Does Pet Insurance Cover Cancer? | GetSkimp',
    metaDescription:
      'Does pet insurance cover cancer? Learn what accident-and-illness plans pay for — chemo, surgery, diagnostics — and when pre-existing rules apply in Texas.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Yes — most accident-and-illness pet insurance policies cover cancer when it is diagnosed after enrollment and after illness waiting periods end, and when it is not considered pre-existing. Covered care often includes diagnostics (blood work, imaging, biopsies), surgery, chemotherapy, hospitalization, and prescription medications related to treatment. Accident-only plans do not cover cancer because it is classified as an illness.',
        ],
      },
      {
        title: 'What cancer treatment usually includes',
        bullets: [
          'Diagnostics — X-rays, ultrasound, MRI/CT when prescribed, fine-needle aspirate, biopsy.',
          'Surgery — tumor removal, limb amputation when medically necessary, specialist referral.',
          'Chemotherapy and some radiation protocols — per policy and benefit schedule.',
          'Hospitalization and nursing care during treatment.',
          'Prescription medications — see [[prescription drug coverage|/knowledge-base/pet/does-pet-insurance-cover-prescription-medications]].',
          'Follow-up exams and lab monitoring tied to the covered diagnosis.',
        ],
      },
      {
        title: 'When cancer is not covered',
        paragraphs: [
          'Cancer is excluded if signs, symptoms, or masses were noted before your policy effective date or during [[waiting periods|/knowledge-base/pet/how-do-pet-insurance-waiting-periods-work]]. Insurers review prior vet records for unexplained weight loss, persistent lumps, or abnormal labs. Related conditions may also be denied if the insurer links them to a pre-existing issue.',
          'Experimental or non-standard treatments, dietary supplements without medical necessity, and costs above your [[annual coverage limit|/knowledge-base/pet/annual-coverage-limits-in-pet-insurance]] are common limits. [[Senior pets|/knowledge-base/pet/pet-insurance-for-senior-pets]] with prior masses on record may face exclusions even for new tumor types — always request a medical history review if unsure.',
        ],
      },
      {
        title: 'Cancer claims and annual limits',
        table: {
          headers: ['Treatment phase', 'Often covered?', 'Limit note'],
          rows: [
            ['Initial diagnosis & staging', 'Yes, if not pre-existing', 'Counts toward annual limit'],
            ['Surgery', 'Yes on accident-and-illness', 'Specialist fees usually eligible'],
            ['Chemotherapy course', 'Yes on most plans', 'Multiple visits can accumulate quickly'],
            ['Palliative care', 'Varies by policy', 'Check benefit schedule'],
            ['Accident-only plan', 'No', 'Illness exclusion applies'],
          ],
        },
      },
      {
        title: 'Protecting coverage before cancer develops',
        paragraphs: [
          'Cancer is one of the most common reasons owners hit their annual cap. Enroll while your pet is healthy, choose an adequate [[annual limit|/knowledge-base/pet/annual-coverage-limits-in-pet-insurance]], and understand [[hereditary cancer risk|/knowledge-base/pet/does-pet-insurance-cover-hereditary-conditions]] in certain breeds. Compare [[accident-and-illness vs. accident-only|/knowledge-base/pet/accident-only-vs-accident-and-illness-pet-insurance]] before buying — cancer protection requires illness coverage.',
        ],
      },
    ],
  },

  'does-pet-insurance-cover-hip-dysplasia': {
    slug: 'does-pet-insurance-cover-hip-dysplasia',
    title: 'Does pet insurance cover hip dysplasia?',
    subtitle:
      'Hip dysplasia can be covered on accident-and-illness plans when symptoms start after enrollment and waiting periods — but orthopedic waits and breed rules vary by carrier.',
    metaTitle: 'Does Pet Insurance Cover Hip Dysplasia? | GetSkimp',
    metaDescription:
      'Does pet insurance cover hip dysplasia? Learn orthopedic waiting periods, breed rules, surgery coverage, and when pre-existing hip issues are excluded in Texas.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Pet insurance can cover hip dysplasia treatment — including diagnostics, medication, physical therapy, and surgery such as total hip replacement — on accident-and-illness plans if the condition is not pre-existing and any orthopedic waiting period has passed. Large breeds (Labradors, German Shepherds, Golden Retrievers) are at higher risk, so enrolling before lameness appears is critical. Some carriers exclude hip dysplasia for specific breeds or require extended waiting periods for all orthopedic conditions.',
        ],
      },
      {
        title: 'What hip dysplasia care may include',
        bullets: [
          'Vet exams, X-rays, and orthopedic specialist consultation.',
          'Pain management and anti-inflammatory prescriptions.',
          'Physical therapy or hydrotherapy when prescribed.',
          'Femoral head ostectomy (FHO) or total hip replacement surgery.',
          'Post-operative follow-up and rehabilitation.',
        ],
      },
      {
        title: 'Orthopedic waiting periods and pre-existing rules',
        paragraphs: [
          'Many policies treat hip and cruciate conditions under stricter [[waiting periods|/knowledge-base/pet/how-do-pet-insurance-waiting-periods-work]] — sometimes six to twelve months from enrollment. Any lameness, bunny-hopping gait, or hip pain noted before enrollment or during that window can cause permanent exclusion.',
          'Hip dysplasia is often [[hereditary|/knowledge-base/pet/does-pet-insurance-cover-hereditary-conditions]] but hereditary status alone does not always mean excluded — timing and symptoms matter more than breed label. Bilateral rules may exclude the second hip if the first was pre-existing. Review [[breed exclusions|/knowledge-base/pet/pet-insurance-breed-exclusions-and-restrictions]] on your quote before buying.',
        ],
      },
      {
        title: 'Coverage factors by scenario',
        table: {
          headers: ['Scenario', 'Typical outcome'],
          rows: [
            ['Enrolled as puppy, no hip signs, wait periods passed', 'Often covered if policy includes orthopedic illness'],
            ['Limping noted on pre-enrollment vet exam', 'Likely excluded as pre-existing'],
            ['Signs appear during orthopedic waiting period', 'Often excluded permanently'],
            ['Accident-only plan', 'Not covered — illness/orthopedic exclusion'],
            ['Breed-specific policy exclusion', 'Not covered regardless of timing'],
          ],
        },
      },
      {
        title: 'Planning for at-risk breeds',
        paragraphs: [
          'If you have a large-breed puppy, enroll early under accident-and-illness coverage with a strong [[annual limit|/knowledge-base/pet/annual-coverage-limits-in-pet-insurance]]. Surgery can cost thousands, and chronic management adds up over years. Pair insurance with weight management and vet-guided exercise — care still must be medically necessary and eligible under your plan. See [[what pet insurance covers|/knowledge-base/pet/what-does-pet-insurance-cover]] and [[how to choose a Texas plan|/knowledge-base/pet/how-to-choose-pet-insurance-in-texas]] for comparison steps.',
        ],
      },
    ],
  },

  'does-pet-insurance-cover-emergency-vet-visits': {
    slug: 'does-pet-insurance-cover-emergency-vet-visits',
    title: 'Does pet insurance cover emergency vet visits?',
    subtitle:
      'Emergency exams, stabilization, surgery, and overnight hospitalization are core benefits of accident-and-illness pet insurance — at your regular vet or a 24-hour ER clinic.',
    metaTitle: 'Does Pet Insurance Cover Emergency Vets? | GetSkimp',
    metaDescription:
      'Does pet insurance cover emergency vet visits? Learn ER exam fees, hospitalization, after-hours care, and accident vs. illness rules on Texas policies.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Yes — accident-and-illness pet insurance generally covers emergency veterinary visits for sudden accidents and unexpected illnesses after applicable [[waiting periods|/knowledge-base/pet/how-do-pet-insurance-waiting-periods-work]] pass. That includes ER exam fees, diagnostics, emergency surgery, fluids, hospitalization, and stabilization. There is no requirement to use a specific emergency hospital network; any licensed U.S. vet or emergency clinic qualifies on most plans, including when you are [[out of state|/knowledge-base/pet/does-pet-insurance-work-out-of-state]].',
        ],
      },
      {
        title: 'What emergency visits typically include',
        bullets: [
          'After-hours and 24/7 emergency clinic exam fees.',
          'Trauma care — hit-by-car, falls, bite wounds, lacerations.',
          'Foreign body ingestion surgery and related imaging.',
          'Acute illness — bloat, urinary blockage, toxin ingestion, heatstroke.',
          'Overnight hospitalization and monitoring.',
          'Emergency diagnostics — blood panels, X-rays, ultrasound.',
        ],
      },
      {
        title: 'Accident vs. illness in the ER',
        paragraphs: [
          'Accidents (injury from a sudden external event) often have shorter waiting periods than illnesses. A dog who tears a cruciate ligament during play may be classified as illness or orthopedic depending on policy definitions — check orthopedic waiting rules. [[Accident-only plans|/knowledge-base/pet/accident-only-vs-accident-and-illness-pet-insurance]] cover ER accidents but not illness emergencies such as pancreatitis or infection.',
          'Exam fees are covered on many modern plans but some older or budget policies exclude the office visit fee while covering treatment — read your benefit schedule. Follow [[claims filing steps|/knowledge-base/pet/how-to-file-a-pet-insurance-claim]] to avoid delays on urgent visits.',
        ],
      },
      {
        title: 'Emergency coverage checklist',
        table: {
          headers: ['Service', 'Usually covered?', 'Note'],
          rows: [
            ['ER exam fee', 'Often yes', 'Confirm on your policy'],
            ['Emergency surgery', 'Yes if eligible event', 'Subject to deductible and limit'],
            ['Hospitalization', 'Yes if eligible', 'Counts toward [[annual limit|/knowledge-base/pet/annual-coverage-limits-in-pet-insurance]]'],
            ['Follow-up rehab', 'Varies', 'May need prescribed therapy'],
            ['Non-emergency walk-in', 'Same rules', 'Must be eligible illness/accident, not wellness'],
          ],
        },
      },
      {
        title: 'Tips for Texas pet owners',
        paragraphs: [
          'Heatstroke, snake bites, and toxic plant ingestion spike ER visits in Texas summers. Keep your insurer\'s app installed for photo uploads from the parking lot. Insurers do not pay the vet upfront on most policies — use a card or CareCredit and file for reimbursement. For households with multiple animals, each pet\'s policy handles its own ER claim — see [[multi-pet coverage|/knowledge-base/pet/multi-pet-insurance-coverage]]. Understand [[what is not covered|/knowledge-base/pet/what-does-pet-insurance-not-cover]] so wellness vaccines or routine exams at an ER clinic are not mistaken for emergencies.',
        ],
      },
    ],
  },

  'does-pet-insurance-cover-prescription-medications': {
    slug: 'does-pet-insurance-cover-prescription-medications',
    title: 'Does pet insurance cover prescription medications?',
    subtitle:
      'Most accident-and-illness policies cover prescription medications prescribed to treat a covered accident or illness — including many chronic drugs when the condition itself is covered.',
    metaTitle: 'Does Pet Insurance Cover Prescriptions? | GetSkimp',
    metaDescription:
      'Does pet insurance cover prescriptions? Learn covered drugs, chronic meds, prescription food rules, and documentation needed for Texas pet insurance claims.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Yes — pet insurance typically covers prescription medications when they are prescribed by a licensed veterinarian to treat a covered accident or illness. That includes antibiotics, pain relievers, chemotherapy drugs, insulin for covered diabetes, and long-term medications for chronic conditions that are not pre-existing. Over-the-counter supplements, flea prevention, and medications for excluded conditions are not covered.',
        ],
      },
      {
        title: 'Medications commonly covered',
        bullets: [
          'Antibiotics and antifungals for covered infections.',
          'Pain and anti-inflammatory drugs after surgery or injury.',
          'Chemotherapy agents for covered [[cancer|/knowledge-base/pet/does-pet-insurance-cover-cancer]] treatment.',
          'Chronic disease meds — thyroid, heart, seizure, allergy — if the disease is covered.',
          'Post-operative antibiotics and sedatives tied to eligible surgery.',
        ],
      },
      {
        title: 'Prescription food and special cases',
        paragraphs: [
          'Prescription diets are covered on some carriers only when used to treat a specific covered condition (for example, urinary stones or kidney disease) and may have a sub-limit. Routine food and supplements are excluded. Medications for [[pre-existing conditions|/knowledge-base/pet/are-pre-existing-conditions-covered-by-pet-insurance]] — such as allergy shots started before enrollment — are denied even if you continue filling the same prescription later.',
          'Heartworm preventive and flea/tick products are usually considered wellness unless treating an active covered infestation — compare [[wellness vs. insurance|/knowledge-base/pet/pet-insurance-vs-wellness-plans]].',
        ],
      },
      {
        title: 'Filing medication claims',
        numbered: [
          'Pay the pharmacy or vet at purchase — insurers rarely bill pharmacies directly.',
          'Keep the prescription label, invoice, and diagnosis on the visit note.',
          'Link the medication to a covered claim or visit when submitting through the portal.',
          'Submit within your insurer\'s time limit — see [[how to file a claim|/knowledge-base/pet/how-to-file-a-pet-insurance-claim]].',
          'Expect reimbursement after deductible and [[reimbursement rate|/knowledge-base/pet/pet-insurance-reimbursement-levels]] apply.',
        ],
      },
      {
        title: 'Medications and annual limits',
        paragraphs: [
          'Ongoing prescriptions for chronic illness can add up over years. Each refill counts toward your [[annual coverage limit|/knowledge-base/pet/annual-coverage-limits-in-pet-insurance]] along with exams and procedures. Enroll before chronic conditions develop, and confirm whether your policy covers compounded medications or online pharmacies. When comparing plans, read the pharmacy benefit section alongside [[emergency|/knowledge-base/pet/does-pet-insurance-cover-emergency-vet-visits]] and [[hereditary condition|/knowledge-base/pet/does-pet-insurance-cover-hereditary-conditions]] rules.',
        ],
      },
    ],
  },

  'pet-insurance-breed-exclusions-and-restrictions': {
    slug: 'pet-insurance-breed-exclusions-and-restrictions',
    title: 'Pet insurance breed exclusions and restrictions',
    subtitle:
      'Some pet insurers limit coverage by breed — excluding certain conditions, declining enrollment, or applying breed-specific waiting rules for dogs and cats prone to costly genetic issues.',
    metaTitle: 'Pet Insurance Breed Exclusions Guide | GetSkimp',
    metaDescription:
      'Pet insurance breed exclusions explained — condition limits, enrollment restrictions, and what to verify in your policy before insuring your dog or cat.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Breed exclusions and restrictions are policy rules that limit or deny coverage based on your pet\'s breed. They can take several forms: declining to insure certain breeds entirely, excluding specific hereditary conditions (hip dysplasia, heart disease), or applying longer waiting periods for orthopedic issues in large breeds. Mixed-breed pets are usually listed as their dominant breed or as mixed breed, which can affect underwriting. Always read breed-specific language in your quote and policy certificate before enrolling.',
        ],
      },
      {
        title: 'Common types of breed restrictions',
        bullets: [
          'Condition-specific exclusions — hip dysplasia excluded for Bulldogs, German Shepherds, etc.',
          'Enrollment declines — rare; some carriers restrict breeds they consider high liability.',
          'Higher underwriting scrutiny — not an exclusion, but more record review for at-risk breeds.',
          'Orthopedic waiting periods — extended waits for all breeds or only large breeds.',
          'Bilateral exclusions — if one knee is pre-existing, both may be excluded.',
        ],
      },
      {
        title: 'Breeds often subject to special rules',
        table: {
          headers: ['Category', 'Examples', 'Typical insurer focus'],
          rows: [
            ['Large breeds', 'Labrador, Golden Retriever, GSD', 'Hip dysplasia, cruciate ligament'],
            ['Brachycephalic', 'Bulldog, Pug, French Bulldog', 'Respiratory, spinal, heat sensitivity'],
            ['Giant breeds', 'Great Dane, Mastiff', 'Cardiac, orthopedic, bloat risk'],
            ['Purebred cats', 'Maine Coon, Siamese', 'Heart disease, urinary issues by breed'],
            ['Mixed breed', 'Listed as mix or best guess', 'Often fewer breed-specific exclusions'],
          ],
        },
      },
      {
        title: 'Breed restrictions vs. pre-existing conditions',
        paragraphs: [
          'A breed exclusion applies based on breed label regardless of your individual pet\'s health. A [[pre-existing condition|/knowledge-base/pet/are-pre-existing-conditions-covered-by-pet-insurance]] applies based on your pet\'s medical history. Your healthy German Shepherd might avoid a breed exclusion for hip dysplasia if the policy covers hereditary hip issues after waiting periods — but a limping exam before enrollment creates a pre-existing exclusion even without a breed rule.',
          'Ask carriers whether [[hereditary conditions|/knowledge-base/pet/does-pet-insurance-cover-hereditary-conditions]] are covered for your breed. Compare multiple quotes in [[how to choose pet insurance in Texas|/knowledge-base/pet/how-to-choose-pet-insurance-in-texas]].',
        ],
      },
      {
        title: 'What to do before you enroll',
        numbered: [
          'Identify your pet\'s breed accurately on the application — mixed breeds matter.',
          'Request written breed exclusion and orthopedic waiting period details.',
          'Enroll young when possible — [[puppy and kitten insurance|/knowledge-base/pet/pet-insurance-for-puppies-and-kittens]] reduces both breed and pre-existing risk.',
          'Keep vet records showing clean orthopedic and cardiac exams at enrollment.',
          'Review [[what is not covered|/knowledge-base/pet/what-does-pet-insurance-not-cover]] alongside breed riders.',
        ],
      },
    ],
  },

  'does-pet-insurance-cover-hereditary-conditions': {
    slug: 'does-pet-insurance-cover-hereditary-conditions',
    title: 'Does pet insurance cover hereditary conditions?',
    subtitle:
      'Hereditary and congenital conditions are often covered on modern accident-and-illness plans if they are not pre-existing — but breed exclusions and waiting periods still apply.',
    metaTitle: 'Does Pet Insurance Cover Hereditary Conditions? | GetSkimp',
    metaDescription:
      'Does pet insurance cover hereditary conditions? Learn genetic vs. congenital rules, breed exclusions, waiting periods, and hip dysplasia coverage in Texas.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Many accident-and-illness pet insurance policies cover hereditary and congenital conditions — health issues linked to genetics or present from birth — when symptoms appear after enrollment and waiting periods, and when the condition is not pre-existing. Coverage varies: some carriers cover all hereditary conditions except those listed as breed exclusions; others exclude specific disorders by breed. Hereditary is not the same as pre-existing — a genetic condition your pet has never shown signs of may still be eligible.',
        ],
      },
      {
        title: 'Hereditary vs. congenital vs. pre-existing',
        table: {
          headers: ['Term', 'Meaning', 'Coverage impact'],
          rows: [
            ['Hereditary', 'Passed genetically; may appear later in life', 'Often covered if not pre-existing and not breed-excluded'],
            ['Congenital', 'Present from birth; may not show symptoms immediately', 'Often covered on modern plans; read policy definition'],
            ['Pre-existing', 'Symptoms before enrollment or during waiting period', 'Excluded on virtually all policies'],
            ['Breed exclusion', 'Carrier rule by breed regardless of your pet\'s health', 'Excluded even without prior symptoms'],
          ],
        },
      },
      {
        title: 'Examples of hereditary conditions',
        bullets: [
          '[[Hip dysplasia|/knowledge-base/pet/does-pet-insurance-cover-hip-dysplasia]] in large dogs.',
          'Heart disease — dilated cardiomyopathy in certain breeds.',
          'Luxating patella, elbow dysplasia, and cruciate ligament disease.',
          'Hereditary eye conditions — cataracts, progressive retinal atrophy.',
          'Blood disorders — von Willebrand disease in Dobermans and similar breeds.',
        ],
      },
      {
        title: 'When hereditary conditions are not covered',
        paragraphs: [
          'Symptoms or vet notes before enrollment make the condition pre-existing even if it is genetic. [[Waiting periods|/knowledge-base/pet/how-do-pet-insurance-waiting-periods-work]], especially orthopedic waits, can exclude conditions that manifest early in the policy. [[Breed exclusions|/knowledge-base/pet/pet-insurance-breed-exclusions-and-restrictions]] may remove specific hereditary disorders for your breed regardless of timing.',
          'Accident-only plans do not cover hereditary illness. Wellness plans do not cover genetic disease treatment — only [[accident-and-illness insurance|/knowledge-base/pet/accident-only-vs-accident-and-illness-pet-insurance]] addresses these costs.',
        ],
      },
      {
        title: 'Maximizing coverage for genetic risk',
        paragraphs: [
          'Enroll while your pet is young and symptom-free. Choose accident-and-illness coverage with a strong [[annual limit|/knowledge-base/pet/annual-coverage-limits-in-pet-insurance]] and confirm hereditary coverage in writing. For breed-prone issues, compare carriers — one may exclude [[hip dysplasia|/knowledge-base/pet/does-pet-insurance-cover-hip-dysplasia]] while another covers it after a twelve-month orthopedic wait. Link hereditary protection with [[cancer|/knowledge-base/pet/does-pet-insurance-cover-cancer]] and [[emergency care|/knowledge-base/pet/does-pet-insurance-cover-emergency-vet-visits]] planning in [[how to choose a Texas plan|/knowledge-base/pet/how-to-choose-pet-insurance-in-texas]].',
        ],
      },
    ],
  },
};

export const PET_INSURANCE_CONFIG: InsuranceProductConfig = {
  lastModified: PET_LAST_MODIFIED,
  sidebarTitle: 'Pet Insurance',
  basePath: PET_BASE_PATH,
  qualifyInsuranceType: 'pet',
  categories: PET_ARTICLE_CATEGORIES,
  contentBySlug: PET_ARTICLE_CONTENT,
};
